import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * A screen of its own below desktop width, over the top bar and tab bar. While a field has
 * focus it ends at the top of the on-screen keyboard, so whatever sits at its bottom stays
 * in reach. Some iPhones send nothing, or old sizes, as the keyboard leaves, so without
 * focus it always goes back to the whole screen.
 */
const FullScreenLayer = ({ isDark, label, children }) => {
  const layerRef = useRef(null);

  useEffect(() => {
    const layer = layerRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const view = window.visualViewport;
    const fit = () => {
      const field = document.activeElement;
      const typing = view && layer.contains(field) && field.matches("input, textarea");
      layer.style.height = typing ? `${view.height}px` : "";
      layer.style.transform = typing ? `translateY(${view.offsetTop}px)` : "";
    };
    // Focus has settled only after the event, once a move between fields is over.
    const fitAfterFocus = () => setTimeout(fit);
    fit();
    layer.addEventListener("focusin", fitAfterFocus);
    layer.addEventListener("focusout", fitAfterFocus);
    view?.addEventListener("resize", fit);
    view?.addEventListener("scroll", fit);
    return () => {
      document.body.style.overflow = previousOverflow;
      layer.removeEventListener("focusin", fitAfterFocus);
      layer.removeEventListener("focusout", fitAfterFocus);
      view?.removeEventListener("resize", fit);
      view?.removeEventListener("scroll", fit);
    };
  }, []);

  return createPortal(
    <div
      ref={layerRef}
      data-theme={isDark ? "dark" : "light"}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-x-0 top-0 z-40 flex h-[100dvh] flex-col bg-bg-raised font-ds text-label-primary"
    >
      {children}
    </div>,
    document.body
  );
};

export default FullScreenLayer;
